import keys from 'constants/keys'
import { getServiceCall, setNotificationServiceCallModal, ServiceCall } from 'redux/actions/serviceCall'
import {
  createServiceNotificationResolver,
  INotificationEventHandler,
} from 'services/notifications/createServiceNotificationResolver'
import { INotification, IResolvedDestination } from 'services/notifications/types'

const CONTENT_UNAVAILABLE_MESSAGE = 'Content is no longer available'

const getString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}

const toPositiveNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value)
  return null
}

const parseServiceCallNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value !== 'string') return null

  const match = value.match(/#?(\d+)/)
  return match ? Number(match[1]) : null
}

const getServiceCallObjectRef = (notification: INotification) => {
  const payloadRef = notification.data?.object_ref
  const objectId = getString(notification.object_ref?.object_id ?? payloadRef?.object_id)
  const contextId = getString(notification.object_ref?.context_id ?? payloadRef?.context_id)
  return { objectId, contextId }
}

// Backend sends the service call's UUID as `object_ref.object_id`, not the numeric `view` +
// `service_call_number` pair the web modal needs — those only exist in Redux state (service calls
// are fetched app-wide on load, see App.tsx). Look the UUID up there instead of going to the network.
const lookupServiceCallByObjectId = (objectId: string) => (_dispatch: any, getState: any) => {
  const list = getState()?.serviceCalls?.serviceCalls
  return Array.isArray(list) ? list.find((call: ServiceCall) => call.service_call_id === objectId) ?? null : null
}

const openServiceCallEvent: INotificationEventHandler = {
  eventTypes: ['service_call_created', 'update_service_call', 'assigned_service_call'],
  // Opens NotificationServiceCallModalHost (mounted at app root) directly — never navigates the
  // page. Cache lookup first (instant, the common case since service calls are already fetched app-
  // wide on load); one best-effort single-record fetch by UUID if it's not cached yet.
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const { objectId, contextId } = getServiceCallObjectRef(notification)
    if (!objectId) return

    const cached: ServiceCall | null = dispatch(lookupServiceCallByObjectId(objectId))
    if (cached) {
      dispatch(setNotificationServiceCallModal({ viewId: cached.view, serviceCallNumber: cached.service_call_number }))
      return
    }

    if (!contextId) {
      dispatch(setNotificationServiceCallModal({ viewId: 0, serviceCallNumber: 0, error: CONTENT_UNAVAILABLE_MESSAGE }))
      return
    }

    dispatch(setNotificationServiceCallModal({ viewId: 0, serviceCallNumber: 0, isLoading: true }))
    const fetched = await dispatch(getServiceCall({ contextId, service_call_id: objectId }))
    dispatch(
      fetched
        ? setNotificationServiceCallModal({ viewId: fetched.view, serviceCallNumber: fetched.service_call_number })
        : setNotificationServiceCallModal({ viewId: 0, serviceCallNumber: 0, error: CONTENT_UNAVAILABLE_MESSAGE })
    )
  },
  async resolve(notification: INotification): Promise<IResolvedDestination> {
    if (notification.target?.route) {
      return { path: notification.target.route, replace: true }
    }

    // onBeforeNavigate already opens the modal directly whenever it has a UUID to work with (even a
    // failed fetch surfaces as an in-modal "unavailable" state) — don't also navigate to the
    // /service-calls list in that case, that's a redundant page load. Only navigate when there's no
    // UUID at all, i.e. we couldn't attempt to open anything from this payload.
    const { objectId } = getServiceCallObjectRef(notification)
    if (objectId) return { handled: true }

    // Older/other payload shapes may carry the numeric ids directly instead of a UUID.
    const payloadRef = notification.data?.object_ref
    const viewId = toPositiveNumber(
      notification.object_ref?.task_view_id ??
        notification.data?.task_view_id ??
        notification.object_ref?.view_id ??
        payloadRef?.view_id ??
        notification.data?.view_id
    )
    const serviceCallNumber = parseServiceCallNumber(
      notification.object_ref?.service_call_number ??
        payloadRef?.service_call_number ??
        notification.data?.service_call_number ??
        notification.body ??
        notification.data?.body
    )

    if (viewId && serviceCallNumber) {
      return { path: keys.ROUTE_NAMES.SVC_ITEM(String(viewId), String(serviceCallNumber)), replace: true }
    }

    return { path: keys.ROUTE_NAMES.SVC, replace: true }
  },
}

const fallbackEvent: INotificationEventHandler = {
  eventTypes: [],
  async resolve(): Promise<IResolvedDestination> {
    return { path: keys.ROUTE_NAMES.SVC, replace: true }
  },
}

export const serviceCallsResolver = createServiceNotificationResolver({
  id: 'service-calls',
  service: 'service_calls',
  events: [openServiceCallEvent],
  fallback: fallbackEvent,
})
